import { useEffect, useState } from "react";
import { Image, Sparkles, Download, Check, ShieldCheck, Paperclip, Save, Trash2, RotateCcw } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/ui-helpers";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const FORMATS = ["Instagram Post", "Instagram Story", "WhatsApp Status", "Facebook", "Website Banner", "A4 Poster"];
const THEMES = [
  { name: "Ocean", cls: "from-blue-600 to-cyan-500" },
  { name: "Sunset", cls: "from-orange-500 to-pink-500" },
  { name: "Royal", cls: "from-indigo-600 to-purple-600" },
  { name: "Forest", cls: "from-emerald-600 to-teal-500" },
];

export default function PosterMaker() {
  const { user } = useAuth();
  const [company, setCompany] = useState(null);
  const [brief, setBrief] = useState("");
  const [format, setFormat] = useState("Instagram Post");
  const [theme, setTheme] = useState(THEMES[0]);
  const [loading, setLoading] = useState(false);
  const [poster, setPoster] = useState(null);
  const [approved, setApproved] = useState(false);
  const [posterImgs, setPosterImgs] = useState([]);
  const [selected, setSelected] = useState(0);
  const [imgLoading, setImgLoading] = useState(false);
  const [quotes, setQuotes] = useState([]);
  const [attaching, setAttaching] = useState(false);
  const [library, setLibrary] = useState([]);
  const [saving, setSaving] = useState(false);

  const posterImg = posterImgs[selected] || null;

  const loadLibrary = () => api.get("/posters/library").then((r) => setLibrary(r.data.posters || [])).catch(() => {});

  const saveToLibrary = async () => {
    if (!posterImg) { toast.error("Generate and select a poster first."); return; }
    if (!approved) { toast.info("Approve the poster before saving it to your library."); return; }
    setSaving(true);
    try {
      await api.post("/posters/library", { title: (poster?.headline || brief || "Poster").slice(0, 80), image: posterImg, brief, format });
      toast.success("Saved to your poster library");
      loadLibrary();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not save the poster."); }
    finally { setSaving(false); }
  };

  const reusePoster = (p) => {
    setPosterImgs([p.data?.image]); setSelected(0); setApproved(true);
    toast.success("Loaded — ready to attach or download");
  };

  const delPoster = async (id) => {
    try { await api.delete(`/records/${id}`); setLibrary((l) => l.filter((x) => x.record_id !== id)); }
    catch (e) { toast.error("Could not delete this poster"); }
  };

  const genImage = async () => {
    if (!brief.trim()) { toast.error("Describe the poster first."); return; }
    setImgLoading(true); setApproved(false); setPosterImgs([]); setSelected(0);
    try {
      const { data } = await api.post("/ai/poster-image", { brief, headline: poster?.headline || "", sub: poster?.sub || "", cta: poster?.cta || "", agency: user?.company_name || company?.google_listing_name || "", format, count: 3 });
      const imgs = data.images || (data.image ? [data.image] : []);
      setPosterImgs(imgs);
      toast.success(`${imgs.length} poster option(s) ready — pick your favourite`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Image generation failed"); }
    finally { setImgLoading(false); }
  };

  useEffect(() => {
    api.get("/company").then((r) => setCompany(r.data.company)).catch(() => {});
    api.get("/records?type=quotation").then((r) => setQuotes(r.data.records || [])).catch(() => {});
    loadLibrary();
  }, []);

  const attachToQuote = async (quoteId) => {
    if (!posterImg) { toast.error("Generate and select a poster first."); return; }
    if (!approved) { toast.info("Approve the poster before attaching it to a quotation."); return; }
    setAttaching(true);
    try {
      await api.post(`/quotations/${quoteId}/attach-poster`, { image: posterImg });
      toast.success("Poster attached — it now appears on the shared quotation page.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not attach the poster."); }
    finally { setAttaching(false); }
  };

  const generate = async () => {
    if (!brief.trim()) { toast.error("Describe the poster content first."); return; }
    setLoading(true); setApproved(false);
    try {
      const prompt = `You are writing marketing poster copy for a seller's store on the Suffle Online marketplace. Format: ${format}. Brief: "${brief}". ` +
        `Respond with EXACTLY three lines and NOTHING else — no preamble, no labels, no markdown, no follow-up question. ` +
        `First line: a punchy headline (max 5 words). Second line: a subheading (max 10 words). Third line: a one-line call to action. ` +
        `Never invent prices, discounts, dates, product names, availability or contact details.`;
      const { data } = await api.post("/ai/chat", { message: prompt, surface: "agent" });
      const clean = (data.reply || "")
        .replace(/\*\*/g, "")
        .split("\n")
        .map((l) => l.replace(/^\s*(line\s*\d[:.\-)]*\s*)/i, "").replace(/^\s*(headline|sub-?heading|subtitle|cta|call to action)\s*[:\-)]*\s*/i, "").replace(/^[-*•\s]+/, "").trim())
        .filter((l) => l && !l.startsWith("---") && !/^(here'?s|sure[,!]|want me|would you|i've|i have|of course)/i.test(l));
      if (clean.length === 0) { toast.error("AI returned an unexpected format — please try again."); return; }
      setPoster({ headline: clean[0] || brief, sub: clean[1] || "", cta: clean[2] || "Contact us to shop" });
      toast.success("Poster copy ready — review & approve");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "AI unavailable"); }
    finally { setLoading(false); }
  };

  return (
    <div>
      <PageHeader title="AI Poster Maker" icon={Image}
        subtitle="Generate on-brand product posters. Your store branding is the single source. No fabricated prices or offers." />

      <div className="grid gap-5 lg:grid-cols-2">
        <div className="card-soft p-5">
          <label className="text-sm font-medium text-slate-600">Describe your poster</label>
          <Textarea value={brief} onChange={(e) => setBrief(e.target.value)} data-testid="poster-brief" className="mt-1" rows={4}
            placeholder="e.g. Promote our Dubai holiday packages for the festive season" />
          <div className="mt-4 grid grid-cols-2 gap-3">
            <div><label className="text-xs text-slate-500">Format</label>
              <Select value={format} onValueChange={setFormat}><SelectTrigger className="mt-1" data-testid="poster-format"><SelectValue /></SelectTrigger>
                <SelectContent>{FORMATS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent></Select></div>
            <div><label className="text-xs text-slate-500">Theme</label>
              <div className="mt-1 flex gap-2">{THEMES.map((t) => (
                <button key={t.name} onClick={() => setTheme(t)} className={`h-9 flex-1 rounded-lg bg-gradient-to-br ${t.cls} ${theme.name === t.name ? "ring-2 ring-offset-2 ring-blue-400" : ""}`} title={t.name} />
              ))}</div></div>
          </div>
          <button onClick={generate} disabled={loading} data-testid="poster-generate"
            className="mt-4 inline-flex items-center gap-2 rounded-full brand-gradient-bg px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50">
            <Sparkles className="h-4 w-4" /> {loading ? "Generating..." : "Generate copy"}
          </button>
          <button onClick={genImage} disabled={imgLoading} data-testid="poster-gen-image"
            className="mt-4 ml-2 inline-flex items-center gap-2 rounded-full border border-purple-300 px-5 py-2.5 text-sm font-semibold text-purple-600 hover:bg-purple-50 disabled:opacity-50">
            <Image className="h-4 w-4" /> {imgLoading ? "Creating 3 options…" : "Create poster images"}
          </button>
          <p className="mt-3 flex items-start gap-1.5 text-xs text-slate-400"><ShieldCheck className="mt-0.5 h-3.5 w-3.5" /> Seller approval is required before sharing commercial content. AI never invents prices, discounts or availability.</p>

          {posterImgs.length > 0 && (
            <div className="mt-5" data-testid="poster-variations">
              <p className="mb-2 text-xs font-semibold text-slate-600">Pick your favourite ({posterImgs.length} options)</p>
              <div className="grid grid-cols-3 gap-2">
                {posterImgs.map((img, i) => (
                  <button key={i} onClick={() => { setSelected(i); setApproved(false); }} data-testid={`poster-variation-${i}`}
                    className={`relative overflow-hidden rounded-xl border-2 transition ${selected === i ? "border-blue-500 ring-2 ring-blue-200" : "border-slate-100 hover:border-slate-300"}`}>
                    <img src={img} alt={`Option ${i + 1}`} className="aspect-square w-full object-cover" />
                    {selected === i && <span className="absolute right-1 top-1 grid h-5 w-5 place-items-center rounded-full bg-blue-500 text-white"><Check className="h-3 w-3" /></span>}
                  </button>
                ))}
              </div>
            </div>
          )}

          {posterImg && (
            <div className="mt-5 space-y-3">
              <button onClick={saveToLibrary} disabled={saving} data-testid="poster-save-library"
                className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:border-purple-300 hover:text-purple-600 disabled:opacity-50">
                <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save to my library"}
              </button>
              {quotes.length > 0 && (
                <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-3" data-testid="poster-attach">
                  <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-600"><Paperclip className="h-3.5 w-3.5" /> Attach this poster to a quotation</label>
                  <Select value="" onValueChange={attachToQuote} disabled={attaching}>
                    <SelectTrigger className="mt-1.5 bg-white" data-testid="poster-attach-quote"><SelectValue placeholder={attaching ? "Attaching…" : "Pick a quotation to brand"} /></SelectTrigger>
                    <SelectContent>{quotes.map((q) => <SelectItem key={q.record_id} value={q.record_id}>{q.title}</SelectItem>)}</SelectContent>
                  </Select>
                  <p className="mt-1.5 text-[11px] text-slate-400">The approved poster appears as a branded banner on the customer's shared quotation page.</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Preview */}
        <div>
          {posterImg && <img src={posterImg} alt="AI poster" className="mb-3 w-full rounded-2xl shadow-xl" data-testid="poster-image" />}
          <div className={`relative flex aspect-square flex-col justify-between overflow-hidden rounded-2xl bg-gradient-to-br ${theme.cls} p-6 text-white shadow-xl`} data-testid="poster-preview">
            <div className="flex items-center justify-between">
              <span className="font-brand text-lg font-bold" data-testid="poster-brand">{user?.company_name || company?.google_listing_name || company?.brand || "Your Store"}</span>
              <span className="rounded-full bg-white/20 px-2 py-0.5 text-[10px]">{format}</span>
            </div>
            <div>
              <h2 className="font-brand text-3xl font-extrabold leading-tight drop-shadow">{poster?.headline || "Your headline appears here"}</h2>
              <p className="mt-2 text-white/90">{poster?.sub || "A subheading generated by your AI Secretary"}</p>
            </div>
            <div className="flex items-end justify-between">
              <div>
                <p className="text-sm font-semibold">{poster?.cta || "Contact us to shop"}</p>
                <p className="mt-1 text-xs text-white/80">{user?.company_name || company?.google_listing_name}</p>
                <p className="text-[10px] text-white/70">{company?.phone} · {company?.website}</p>
              </div>
              <span className="text-[10px] text-white/70">Powered by Suffle Online</span>
            </div>
          </div>
          <div className="mt-3 flex gap-2">
            <button onClick={() => setApproved(true)} data-testid="poster-approve" className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 px-4 py-2 text-sm font-medium text-emerald-600 hover:bg-emerald-50">
              <Check className="h-4 w-4" /> {approved ? "Approved" : "Approve"}
            </button>
            <button onClick={() => { if (!approved) { toast.info("Approve the poster before downloading."); return; } if (posterImg) { const a = document.createElement("a"); a.href = posterImg; a.download = "suffle-poster.png"; a.click(); } else window.print(); }} data-testid="poster-download" className="inline-flex items-center gap-1.5 rounded-full brand-gradient-bg px-4 py-2 text-sm font-semibold text-white">
              <Download className="h-4 w-4" /> Download / Share
            </button>
          </div>
        </div>
      </div>

      {library.length > 0 && (
        <div className="card-soft mt-5 p-5" data-testid="poster-library">
          <h3 className="mb-3 flex items-center gap-2 font-brand font-semibold text-slate-800"><Save className="h-4 w-4 text-purple-500" /> My Poster Library <span className="text-xs font-normal text-slate-400">({library.length})</span></h3>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6">
            {library.map((p) => (
              <div key={p.record_id} className="group relative overflow-hidden rounded-xl border border-slate-100" data-testid={`library-poster-${p.record_id}`}>
                <img src={p.data?.image} alt={p.title} className="aspect-square w-full object-cover" />
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/50 opacity-0 transition group-hover:opacity-100">
                  <button onClick={() => reusePoster(p)} data-testid={`library-reuse-${p.record_id}`} className="inline-flex items-center gap-1 rounded-full bg-white px-3 py-1 text-[11px] font-semibold text-slate-700"><RotateCcw className="h-3 w-3" /> Use</button>
                  <button onClick={() => delPoster(p.record_id)} data-testid={`library-delete-${p.record_id}`} className="inline-flex items-center gap-1 rounded-full bg-rose-500 px-3 py-1 text-[11px] font-semibold text-white"><Trash2 className="h-3 w-3" /> Delete</button>
                </div>
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-1.5"><p className="truncate text-[10px] font-medium text-white">{p.title}</p></div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
